import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, BookOpen, Target, CalendarCheck, Trophy, Flame } from "lucide-react";
import { api } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { WordIcon } from "@/components/WordIcon";

const fmt = (d) => new Date(d + "T12:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "long" });

export default function RecapPage() {
  const [r, setR] = useState(null);
  useEffect(() => {
    api.get("/recap/weekly").then((res) => {
      setR(res.data);
      localStorage.setItem("btp_recap_seen", res.data.week_start);
    });
  }, []);
  if (!r) return <Skeleton className="h-64 w-full" />;

  const stats = [
    [BookOpen, r.lessons, "leçons", "recap-lessons"],
    [Target, `${r.average}%`, "réussite", "recap-average"],
    [CalendarCheck, r.days_active, "jours actifs", "recap-days"],
    [Flame, r.streak, "série actuelle", "recap-streak"],
  ];

  return (
    <div className="space-y-6" data-testid="recap-page">
      <Link to="/" data-testid="recap-back-btn" className="inline-flex items-center gap-1 font-semibold text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-5 h-5" /> Accueil
      </Link>
      <div className="rounded-2xl bg-slate-900 text-white overflow-hidden rise">
        <div className="hazard-stripe h-2" />
        <div className="p-5">
          <p className="text-xs font-bold uppercase tracking-widest text-amber-400">Bilan de la semaine</p>
          <h1 className="font-display text-3xl font-extrabold uppercase" data-testid="recap-week">Du {fmt(r.week_start)} au {fmt(r.week_end)}</h1>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 rise rise-1">
        {stats.map(([Icon, v, l, id]) => (
          <div key={id} className="bg-white rounded-xl border-2 border-slate-200 p-4">
            <Icon className="w-5 h-5 text-orange-500" />
            <div className="font-display text-4xl font-bold" data-testid={id}>{v}</div>
            <div className="text-xs font-bold uppercase text-slate-500">{l}</div>
          </div>
        ))}
      </div>

      {r.trophy && (
        <div className="rounded-2xl bg-amber-100 border-2 border-amber-300 p-4 flex items-center gap-3 text-amber-900 rise rise-2" data-testid="recap-trophy">
          <Trophy className="w-10 h-10" />
          <p className="font-bold">Trophée d'équipe gagné : {r.trophy.done}/{r.trophy.target} leçons ensemble !</p>
        </div>
      )}

      <section className="space-y-2 rise rise-2" data-testid="recap-words">
        <p className="text-xs font-bold uppercase text-slate-500">{r.words_count} mot(s) appris</p>
        {r.words.length === 0 ? (
          <p className="text-slate-600" data-testid="recap-empty">Aucune leçon la semaine dernière. Cette semaine, c'est reparti !</p>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {r.words.map((w, i) => (
              <div key={w.word} className="bg-white rounded-xl border-2 border-slate-200 p-3 flex items-center gap-2" data-testid={`recap-word-${i}`}>
                <WordIcon word={w} size="sm" />
                <span className="font-bold text-sm">{w.word}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      <Link to="/" data-testid="recap-continue-btn" className="w-full h-14 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-display text-xl font-bold uppercase flex items-center justify-center transition-colors">
        Commencer la semaine
      </Link>
    </div>
  );
}
